import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { queryClient, QueryClientProvider } from '@/lib/queryClient';
import Login from '@/pages/Login';
import ErrorBoundary from '@/components/shared/ErrorBoundary';
import TelegramGate from '@/components/shared/TelegramGate';
import PhoneGate from '@/components/shared/PhoneGate';
import { LanguageProvider } from '@/i18n';
import { Toaster } from 'sonner';
import '@/App.css';

const MspMissingMappingsDialog = lazy(() => import('@/components/msp/MspMissingMappingsDialog'));

// Dashboard pages are lazy-loaded so each user's role only downloads the
// code they actually need. Cuts the first-paint bundle from ~ 800 KB to
// ~ 200 KB (P2 audit fix: bundle splitting). Login stays eager so the very
// first paint (the most common path) has zero loading flash.
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const ManagerDashboard = lazy(() => import('@/pages/ManagerDashboard'));
const HeadDashboard = lazy(() => import('@/pages/HeadDashboard'));
const PowerUserDashboard = lazy(() => import('@/pages/PowerUserDashboard'));
const PromoterDashboardPage = lazy(() => import('@/pages/PromoterDashboard'));
const PromoterInchargeDashboard = lazy(() => import('@/pages/PromoterInchargeDashboard'));
const CrmDashboard = lazy(() => import('@/pages/CrmDashboard'));
const POManagerDashboard = lazy(() => import('@/pages/POManagerDashboard'));
const GrowthDashboard = lazy(() => import('@/pages/GrowthDashboard'));
const MobileHeadDashboard = lazy(() => import('@/pages/MobileHeadDashboard'));
const CreatorsLanding = lazy(() => import('@/pages/creators/CreatorsLanding'));
const CreatorRegister = lazy(() => import('@/pages/creators/register/CreatorRegister'));
const CreatorPortal = lazy(() => import('@/pages/creator/CreatorPortal'));
const CreatorNetwork = lazy(() => import('@/pages/creatorNetwork/CreatorNetwork'));

const DashboardLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="w-8 h-8 mx-auto mb-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">Loading…</p>
    </div>
  </div>
);

// Role constants extracted to avoid re-renders from inline arrays
const ADMIN_ROLES = ['admin', 'vp_insta'];
const MANAGER_ROLES = ['manager', 'manager_mobile', 'manager_accounts'];
const HEAD_ROLES = ['head', 'head_mobile', 'head_marketing', 'head_accounts'];
const POWER_ROLES = ['power_user'];
const PROMOTER_ROLES = ['promoter'];
const INCHARGE_ROLES = ['promoter_incharge'];
const CRM_ROLES = ['crm', 'admin'];
const PO_ROLES = ['po_manager', 'admin'];
const GROWTH_ROLES = ['growth_manager', 'admin'];

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const ROLE_REDIRECT_MAP = {
  admin: '/admin',
  crm: '/crm',
  manager: '/manager',
  manager_mobile: '/manager',
  manager_accounts: '/manager',
  power_user: '/power',
  promoter: '/promoter',
  promoter_incharge: '/incharge',
  head: '/head',
  head_mobile: '/head',
  head_marketing: '/head',
  head_accounts: '/head',
  po_manager: '/po',
  growth_manager: '/growth',
  vp_insta: '/admin/bot/dashboard',
  mobile_sales_head: '/mobile-head',
  creator_manager: '/creator-network',
};

const AppRoutes = () => {
  const { user } = useAuth();

  const rootRedirect = user
    ? ROLE_REDIRECT_MAP[user.role] || '/head'
    : '/login';

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/creators" element={<CreatorsLanding />} />
      <Route path="/creators/register" element={<CreatorRegister />} />
      <Route path="/creator/*" element={<CreatorPortal />} />
      <Route
        path="/creator-network/*"
        element={
          <ProtectedRoute>
            <CreatorNetwork />
          </ProtectedRoute>
        }
      />
      <Route path="/owner" element={<Navigate to="/admin" replace />} />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={ADMIN_ROLES}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/*"
        element={
          <ProtectedRoute allowedRoles={MANAGER_ROLES}>
            <ManagerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mobile-head/*"
        element={
          <ProtectedRoute allowedRoles={['mobile_sales_head', 'admin']}>
            <MobileHeadDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/head/*"
        element={
          <ProtectedRoute allowedRoles={HEAD_ROLES}>
            <HeadDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/power/*"
        element={
          <ProtectedRoute allowedRoles={POWER_ROLES}>
            <PowerUserDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/promoter/*"
        element={
          <ProtectedRoute allowedRoles={PROMOTER_ROLES}>
            <PromoterDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/incharge/*"
        element={
          <ProtectedRoute allowedRoles={INCHARGE_ROLES}>
            <PromoterInchargeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/*"
        element={
          <ProtectedRoute allowedRoles={CRM_ROLES}>
            <CrmDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/po/*"
        element={
          <ProtectedRoute allowedRoles={PO_ROLES}>
            <POManagerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/growth/*"
        element={
          <ProtectedRoute allowedRoles={GROWTH_ROLES}>
            <GrowthDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to={rootRedirect} replace />} />
    </Routes>
  );
};

/* Floating badge; inside the router so it can step above the creator portal's mobile tab bar. */
const PORTAL_RE = /^\/creator(\/(?!login|forgot-password|reset-password)|$)/;
function PoweredBy() {
  const { pathname } = useLocation();
  return <div className={`vp-powered ${PORTAL_RE.test(pathname) ? 'is-creator-portal' : ''}`} data-testid="powered-by-badge">Powered by AnimalAI</div>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <LanguageProvider>
            <ErrorBoundary>
              <Suspense fallback={<DashboardLoading />}>
                <PhoneGate>
                <TelegramGate>
                  <AppRoutes />
                </TelegramGate>
                </PhoneGate>
              </Suspense>
              {/* Own Suspense, outside the routes' one: lazy route loads must not unmount the pop-up */}
              <Suspense fallback={null}><MspMissingMappingsDialog /></Suspense>
            </ErrorBoundary>
            <Toaster position="top-right" richColors />
            </LanguageProvider>
          </AuthProvider>
        </QueryClientProvider>
        <PoweredBy />
      </BrowserRouter>
    </div>
  );
}

export default App;
